import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { initGA, usePageviewTracking } from "./utils/analytics";
import HomepageSwitcher from "./pages/gd/HomepageSwitcher";
import ExclusiveDealsListingV2 from "./pages/gd/v2/ExclusiveDealsListing";
import ListingPageSwitcher from "./pages/gd/ListingPageSwitcher";
import Header from "./pages/gd/Header";
import Footer from "./pages/gd/Footer";
import HeaderV2 from "./pages/gd/v2/Header";
import FooterV2 from "./pages/gd/v2/Footer";
import ShowPage from "./pages/gd/ShowPage";
import PostsShowPage from "./pages/gd/PostsShowPage";
import MiceAboutPage from "./pages/gd/MiceAboutPage";
import CorporateMicePage from "./pages/gd/CorporateMicePage";
import CorporateBusinessTravelPage from "./pages/gd/CorporateBusinessTravelPage";
import CorporateGdWarrantPage from "./pages/gd/CorporateGdWarrantPage";
import NewsAndBlogsPage from "./pages/gd/NewsAndBlogsPage";
import NewsAndBlogsShowPage from "./pages/gd/NewsAndBlogsShowPage";
import GalleryPage from "./pages/gd/GalleryPage";
import GalleryShowPage from "./pages/gd/GalleryShowPage";
import PartnerSignInPage from "./pages/gd/PartnerSignInPage";
import AboutUsPage from "./pages/gd/AboutUsPage";
import ContactUsPage from "./pages/gd/ContactUsPage";
import ContactAgentsPage from "./pages/gd/ContactAgentsPage";
import GDFlightContactAgentsPage from "./pages/gd/GDFlightContactAgentsPage";
import GDFlightAboutPage from "./pages/gd/GDFlightAboutPage";
import WhatsNewPage from "./pages/gd/WhatsNewPage";
import WhatsNewShowPage from "./pages/gd/WhatsNewShowPage";
import SeriesPage from "./pages/gd/SeriesPage";
import SeriesShowPage from "./pages/gd/SeriesShowPage";
import FooterStaticPage from "./pages/gd/FooterStaticPage";
import PostShowPage from "./pages/gd/PostShowPage";
import IceVisaContactAgentsPage from "./pages/gd/IceVisaContactAgentsPage";
import ResetPasswordPage from "./pages/gd/ResetPasswordPage";
import ScrollToTop from "./layouts/ScrollToTop";
import FloatingChatButton from "./layouts/FloatingChatButton";
// Import your new Voice Over Ad player component here
import VoiceOverAdButton from "./layouts/VoiceOverAdButton";
import PostsPage from "./pages/gd/PostsPage";
import BookingsPage from "./pages/gd/BookingsPage";
import PaymentStatus from "./pages/gd/PaymentStatus";
import SignInPage from "./pages/gd/SignInPage";
import ProfilePage from "./pages/gd/ProfilePage";
import InvalidReferralLink from "./components/shared/InvalidReferralLink";
import OrderInformationPage from "./pages/gd/OrderInformationPage";
import DedicatedPaymentPage from "./pages/gd/DedicatedPaymentPage";
import ExpiredLinkPage from "./pages/gd/ExpiredLinkPage";
import B2C2BApi from "./api-services/b2c2b-api.service";
import Cookies from "js-cookie"
import MainLayout from "./layouts/gd/MainLayout";
import MagazinePage from "./pages/gd/MagazinePage"
import MagazineReaderPage from "./pages/gd/MagazineReaderPage";
import { AuthProvider } from "./services/AuthContext";
import { InsuranceProvider } from "./services/InsuranceContext";
import { SettingsProvider, useIsNewLayout, useSettings } from "./services/SettingsContext";
import { isB2C2B, SIGNIN_URL } from "./utils/helpers";

const customCookies = Cookies.withAttributes({
  expires: 14
});

function LayoutWrapper(props) {
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasVoiceOverAd, setHasVoiceOverAd] = useState(false);
  const isItineraryShowPage = location.pathname.includes("itinerary");
  const isHomepage = location.pathname === "/web" || location.pathname === "/web/";
  const isListing = location.pathname === "/web/listing";
  const useV2Chrome = useIsNewLayout() && (isHomepage || isListing);

  // Exact sizes
  const BTN = 54;
  const GAP = 12; // Tight gap setting
  const chatBottomBase = isItineraryShowPage ? 100 : 24;

  // Lock the position to sit directly above the chat button at all times
  const voiceOverBottom = chatBottomBase + BTN + GAP; // 24 + 54 + 12 = 90px

  return (
    <>
      {useV2Chrome ? (
        <MainLayout
          Header={<HeaderV2 serverSideName={props.username} isPartner={props.isPartner} />}
          Footer={<FooterV2 />}
        />
      ) : (
        <MainLayout
          Header={<Header serverSideName={props.username} isPartner={props.isPartner} />}
          Footer={<Footer />}
        />
      )}

      {/* Floating Elements Stack */}
      {isHomepage && !isChatOpen && (
        <VoiceOverAdButton
          bottomOffset={voiceOverBottom}
          onVisibilityChange={setHasVoiceOverAd}
        />
      )}

      <FloatingChatButton
        isItineraryShowPage={isItineraryShowPage}
        showBackTop={true}
        bottomBase={chatBottomBase}
        onToggle={setIsChatOpen}
        hasVoiceOverAd={isHomepage && !isChatOpen && hasVoiceOverAd}
      />
    </>
  );
}

// Layout for the isolated /web/v2 tree. Reuses MainLayout but with the
// redesigned (v2) header/footer, so nothing in the existing /web layout changes.
function V2LayoutWrapper(props) {
  return (
    <MainLayout
      Header={<HeaderV2 serverSideName={props.username} isPartner={props.isPartner} />}
      Footer={<FooterV2 />}
    />
  );
}

export default function gd(props: {isUserSignedIn:boolean, username: string, isPartner: boolean, gd_policy: string, gd_benefits: string, gd_endorsement: string, google_tracking_id?: string}) {
  initGA(props.google_tracking_id);

  const ProtectedRoute = () => {
    if (!props.isUserSignedIn) {
      return <Navigate to={SIGNIN_URL} replace />;
    }
  
    return <LayoutWrapper {...props} />;
  };

  const Inner = () =>{
    const location = useLocation();
    
    const [searchParams] =  useSearchParams();
    const navigate =  useNavigate();

    const { loading: settingsLoading } = useSettings();

    usePageviewTracking();

    useEffect(() => {
      let active = true;
      const agencyRef = searchParams.get('ref');
      const keyword = searchParams.get('keyword');
    
      if (location.hash) {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          window.history.replaceState("", document.title, location.pathname);
        }
      }
    
      if (agencyRef) {
        B2C2BApi.validateAgentReferralLink(agencyRef, keyword)
          .then(referral => {
            if (active && referral) {
              const agencyAttributes = referral?.agency?.attributes || {};
              const referralEntityType = agencyAttributes.referral_entity_type;
              const masterAgent = agencyAttributes.master_agent;
              const agency = {
                id: referral?.agency?.id,
                name: agencyAttributes.name,
                full_name: agencyAttributes.full_name,
                emails: agencyAttributes.emails,
                phones: agencyAttributes.phones,
                address: agencyAttributes.address,
                city: agencyAttributes.city,
                state: agencyAttributes.state,
                masterAgent,
                subagent: referralEntityType === "subagent" ? masterAgent : null,
                referralEntityType,
                referralEntityId: agencyAttributes.referral_entity_id,
                referralEntityName: agencyAttributes.referral_entity_name,
                link: window.location.href
              };
              customCookies.set("agency", JSON.stringify(agency));
              B2C2BApi.trackClick(agencyRef, keyword).catch(() => {});

              if (!location.pathname.includes("itinerary")) {
                if (!keyword) {
                  navigate("/web");
                  return;
                }
                navigate(`/web/listing?keyword=${keyword}`);
              }
            }
          }).catch(error => {
            if (active) {
              navigate('/web/invalid-link');
            }
          });
      }
    
      return () => {
        active = false;
      };
    }, [location.hash, location.pathname, searchParams, navigate]);

    const isVersionGatedRoute =
      location.pathname === "/web" || location.pathname === "/web/" || location.pathname === "/web/listing";
    if (isVersionGatedRoute && settingsLoading) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <Routes>
        {/* Isolated v2 tree, still used by pages with no v1 equivalent to
            switch back to (e.g. exclusive deals). Homepage/listing now render
            through the switcher components below instead of a separate tree. */}
        <Route path="/web/v2" element={<V2LayoutWrapper {...props} />}>
          <Route path="/web/v2/exclusive-deals" element={<ExclusiveDealsListingV2 />} />
        </Route>

        <Route path="/web" element={<LayoutWrapper {...props} />}>
          <Route index element={<HomepageSwitcher />} />
          <Route path="/web/listing" element={<ListingPageSwitcher />} />
          <Route path="/web/itinerary/:id" element={<ShowPage isPartner={props.isPartner} />} />
          <Route path="/web/series-tour/:type" element={<SeriesPage />} />
          <Route path="/web/series-tour/:type/:id" element={<SeriesShowPage />} /> 
          <Route path="/web/posts/:type" element={<PostsPage />} />
          <Route path="/web/posts/:type/:id" element={<PostsShowPage />} />
          <Route path="/web/mice/about" element={<MiceAboutPage />} />
          <Route path="/web/corporate/mice" element={<CorporateMicePage />} />
          <Route path="/web/corporate/business-travel" element={<CorporateBusinessTravelPage />} />
          <Route path="/web/corporate/gd-warrant" element={<CorporateGdWarrantPage />} />
          <Route path="/web/post/:type" element={<PostShowPage />} />
          <Route path="/web/magazine" element={<MagazinePage />} />
          <Route path="/web/magazine/:slug" element={<MagazineReaderPage />}/>
          <Route path="/web/ice-visa-services-agents" element={<IceVisaContactAgentsPage />} />
          <Route path="/web/blogs" element={<NewsAndBlogsPage />} />
          <Route path="/web/blogs/:id" element={<NewsAndBlogsShowPage />} />
          <Route path="/web/gallery" element={<GalleryPage />} />
          <Route path="/web/gallery/:id" element={<GalleryShowPage />} />
          <Route path="/web/new-posts" element={<WhatsNewPage />} />
          <Route path="/web/new-posts/:id" element={<WhatsNewShowPage />} />
          
          <Route path="/web/partner-sign-in" element={<PartnerSignInPage />} />
          <Route path="/web/about-us" element={<AboutUsPage />} />
          <Route path="/web/contact-us" element={<ContactUsPage />} />
          <Route path="/web/contact-agents" element={<ContactAgentsPage />} />
          <Route path="/web/gd-flight-about-us" element={<GDFlightAboutPage />} />
          <Route path="/web/gd-flight-agents" element={<GDFlightContactAgentsPage />} />
          <Route path="/web/footer/:type" element={<FooterStaticPage />} />
          <Route path="/web/:scope/reset-password" element={<ResetPasswordPage />} />

          {
            isB2C2B && (
              <>
                <Route path="/web/sign-in" element={<SignInPage />} />
                <Route path="/web/payments/:id" element={<PaymentStatus />} />
                <Route path="/web/bookings/:id/payment" element={<DedicatedPaymentPage />} />
                <Route path="/web/invalid-link" element={<InvalidReferralLink />} />
                <Route path="/web/expired-link" element={<ExpiredLinkPage />} />
              </>
            )
          }
        </Route>
      </Routes>
    )
  
  }

  return (
      <BrowserRouter>
       <AuthProvider>
        <SettingsProvider>
            <ScrollToTop/>
            <Inner/>

            {/* separate layout for protected routes */}
            <InsuranceProvider value={{ gd_policy: props.gd_policy, gd_benefits: props.gd_benefits, gd_endorsement: props.gd_endorsement }}>
              <Routes>
                <Route element={<ProtectedRoute />}>
                  <Route path="/web/profile" element={<ProfilePage />}/>   
                  <Route path="/web/bookings" element={<BookingsPage />} />
                  <Route path="/web/bookings/:id" element={<OrderInformationPage />} />         
                </Route>
              </Routes>
            </InsuranceProvider>
        </SettingsProvider>
       </AuthProvider>
      </BrowserRouter>
  );
}
